import React, { useContext, useEffect, useState } from 'react';
import { getContractHistory } from 'app/api/contractApi';
import { ToastContext } from 'app/context/ToastContext';
import { DataTable } from 'primereact/datatable';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { Dialog } from 'primereact/dialog';
import { formatDateFromInstant, formatFromLocalDate } from 'app/shared/util/date-utils';

const FIELD_LABELS: Record<string, string> = {
  contractNr: 'Číslo zmluvy',
  approved: 'Zmluva schválená',
  proposalNr: 'Číslo návrhu',
  state: 'Stav zmluvy',
  policyHolder: 'Poistník',
  product: 'Produkt',
  concludedBy: 'Zmluvu uzatvoril',
  careerForConcludedBy: 'Pozícia uzatvárajúceho',
  provisionForTipster: 'Provízia pre tipéra (%)',
  tipster: 'Tipér',
  careerForTipster: 'Pozícia tipéra',
  dispatchNote: 'Sprievodka',
  dateOfSignature: 'Dátum podpisu',
  insuranceStartAt: 'Dátum začiatku poistenia',
  insuranceEndAt: 'Dátum konca poistenia',
  insuranceSum: 'Celkové poistné',
  insuranceSumDomacnost: 'Poistná suma domácnosť',
  insuranceSumNehnutelnost: 'Poistná suma nehnuteľnosť',
  householdType: 'Typ domácnosti',
  amountOfLoan: 'Výška úveru',
  dobaSplatnosti: 'Doba splatnosti',
  dobaFixacie: 'Doba fixácie',
  vyskaPoplatku: 'Výška poplatku',
  paymentPayedType: 'Poplatok uhradený',
  goalSum: 'Cieľová suma',
  znacka: 'Značka',
  model: 'Model',
  spz: 'ŠPZ',
  vin: 'VIN',
  objem: 'Objem',
  vykon: 'Výkon',
  hmotnost: 'Hmotnosť',
  farba: 'Farba',
  contractPaymentTemplates: 'Platby',
  insuredClients: 'Poistenci',
  additionalInsurances: 'Pripoistenia',
  payment: 'Výška platby',
  yearlyPayment: 'Ročná platba',
  validFrom: 'Platí od',
  validTo: 'Platí do',
  paymentFrequency: 'Frekvencia platenia',
  productAdditionalInsurance: 'Pripoistenie',
  insuranceBonus: 'Poistné',
  period: 'Obdobie',
};

const labelFor = (key: string) => FIELD_LABELS[key] ?? key;

const isLocalDateString = (v: any) => typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v);

const objectDisplay = (value: any): string => {
  const id = value?.id ?? null;
  const personName = [value?.firstName, value?.lastName].filter(Boolean).join(' ').trim();
  const display = value?.name || value?.documentName || personName || null;
  if (display && id != null) return `${display} (#${id})`;
  if (display) return display;
  if (id != null) return `#${id}`;
  return JSON.stringify(value);
};

const formatValue = (value: any): string => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Áno' : 'Nie';
  if (isLocalDateString(value)) return formatFromLocalDate(value);
  if (typeof value === 'object') return objectDisplay(value);
  return String(value);
};

const isCollectionDiff = (v: any) =>
  v && typeof v === 'object' && !Array.isArray(v) && ('added' in v || 'removed' in v || 'modified' in v);

const OLD_BG = 'rgba(231, 76, 60, 0.08)';
const NEW_BG = 'rgba(46, 204, 113, 0.10)';
const CELL_BORDER = '1px solid var(--surface-border, #e5e7eb)';
const TH_BG = 'var(--surface-100, #f3f4f6)';

const cellStyle: React.CSSProperties = { padding: '0.5rem 0.75rem', border: CELL_BORDER, verticalAlign: 'top' };
const thStyle: React.CSSProperties = { ...cellStyle, background: TH_BG, textAlign: 'left', fontWeight: 600 };
const tableStyle: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', marginBottom: '1rem' };

const ItemFieldsList = ({ item }: { item: Record<string, any> }) => {
  const entries = Object.entries(item || {}).filter(([k]) => k !== 'id');
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem' }}>
      {item?.id != null && (
        <div style={{ color: 'var(--text-color-secondary, #6b7280)', fontSize: '0.85rem' }}>ID: #{item.id}</div>
      )}
      {entries.map(([k, v]) => (
        <div key={k}>
          <strong>{labelFor(k)}:</strong> {formatValue(v)}
        </div>
      ))}
      {entries.length === 0 && item?.id == null && <div>—</div>}
    </div>
  );
};

const ModifiedItemTable = ({ changedProperties }: { changedProperties: Record<string, [any, any]> }) => (
  <table style={tableStyle}>
    <thead>
      <tr>
        <th style={thStyle}>Pole</th>
        <th style={thStyle}>Pôvodná hodnota</th>
        <th style={thStyle}>Nová hodnota</th>
      </tr>
    </thead>
    <tbody>
      {Object.entries(changedProperties).map(([field, [oldV, newV]]) => (
        <tr key={field}>
          <td style={cellStyle}>{labelFor(field)}</td>
          <td style={{ ...cellStyle, background: OLD_BG }}>{formatValue(oldV)}</td>
          <td style={{ ...cellStyle, background: NEW_BG }}>{formatValue(newV)}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const CollectionDiffSection = ({ fieldKey, diff }: { fieldKey: string; diff: any }) => {
  const added: any[] = diff?.added ?? [];
  const removed: any[] = diff?.removed ?? [];
  const modified: any[] = diff?.modified ?? [];
  if (added.length === 0 && removed.length === 0 && modified.length === 0) return null;

  return (
    <div style={{ marginTop: '1rem' }}>
      <h4 style={{ margin: '0 0 0.5rem 0' }}>{labelFor(fieldKey)}</h4>

      {added.length > 0 && (
        <div style={{ marginBottom: '0.75rem' }}>
          <div style={{ fontWeight: 600, color: 'rgb(34, 139, 34)', marginBottom: '0.25rem' }}>
            Pridané ({added.length})
          </div>
          <table style={tableStyle}>
            <tbody>
              {added.map((item, idx) => (
                <tr key={idx}>
                  <td style={{ ...cellStyle, width: '4rem', fontWeight: 600 }}>#{idx + 1}</td>
                  <td style={{ ...cellStyle, background: NEW_BG }}>
                    <ItemFieldsList item={item} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {removed.length > 0 && (
        <div style={{ marginBottom: '0.75rem' }}>
          <div style={{ fontWeight: 600, color: 'rgb(192, 57, 43)', marginBottom: '0.25rem' }}>
            Odstránené ({removed.length})
          </div>
          <table style={tableStyle}>
            <tbody>
              {removed.map((item, idx) => (
                <tr key={idx}>
                  <td style={{ ...cellStyle, width: '4rem', fontWeight: 600 }}>#{idx + 1}</td>
                  <td style={{ ...cellStyle, background: OLD_BG }}>
                    <ItemFieldsList item={item} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modified.length > 0 && (
        <div style={{ marginBottom: '0.75rem' }}>
          <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>Upravené ({modified.length})</div>
          {modified.map((m, idx) => (
            <div key={idx} style={{ marginBottom: '0.5rem' }}>
              <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>
                #{idx + 1}
                {m?.child?.id != null && (
                  <span style={{ color: 'var(--text-color-secondary, #6b7280)', fontWeight: 400 }}> (ID: #{m.child.id})</span>
                )}
              </div>
              <ModifiedItemTable changedProperties={m?.changedProperties ?? {}} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const RevisionChanges = ({ revision }: { revision: any }) => {
  const items: Array<{ key: string; value: any }> = revision?.changes ?? [];
  const simple = items.filter(c => !isCollectionDiff(c.value));
  const collections = items.filter(c => isCollectionDiff(c.value));

  return (
    <div>
      <div style={{ marginBottom: '1rem', display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: '0.75rem', rowGap: '0.25rem' }}>
        <strong>ID revízie:</strong>
        <span>{revision.revisionId}</span>
        <strong>Typ:</strong>
        <span>{revision._typeLabel}</span>
        <strong>Dátum:</strong>
        <span>{formatDateFromInstant(revision.modifiedAt)}</span>
        <strong>Modifikoval:</strong>
        <span>{revision.modifiedBy ?? '—'}</span>
      </div>

      {simple.length > 0 ? (
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Pole</th>
              <th style={thStyle}>Pôvodná hodnota</th>
              <th style={thStyle}>Nová hodnota</th>
            </tr>
          </thead>
          <tbody>
            {simple.map(c => {
              const [oldV, newV] = Array.isArray(c.value) ? c.value : [undefined, undefined];
              return (
                <tr key={c.key}>
                  <td style={cellStyle}>{labelFor(c.key)}</td>
                  <td style={{ ...cellStyle, background: OLD_BG }}>{formatValue(oldV)}</td>
                  <td style={{ ...cellStyle, background: NEW_BG }}>{formatValue(newV)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        collections.length === 0 && <div>Žiadne zmeny.</div>
      )}

      {collections.map(c => (
        <CollectionDiffSection key={c.key} fieldKey={c.key} diff={c.value} />
      ))}
    </div>
  );
};

const ContractHistoryTable = ({ contractId }) => {
  const { showError } = useContext(ToastContext);

  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRevision, setSelectedRevision] = useState<any>(null);
  const [dialogVisible, setDialogVisible] = useState(false);

  useEffect(() => {
    getContractHistory(contractId)
      .then(response => {
        setHistory(response.data);
      })
      .catch(e => {
        console.error(e);
        showError('Chyba', e.response?.data?.detail);
      })
      .finally(() => setLoading(false));
  }, [contractId]);

  const formatType = t => {
    if (t === 0) return 'Vytvorené';
    if (t === 1) return 'Upravené';
    if (t === 2) return 'Vymazané';
    return '-';
  };

  const showChanges = revision => {
    setSelectedRevision({ ...revision, _typeLabel: formatType(revision.type) });
    setDialogVisible(true);
  };

  const actionBodyTemplate = rowData => {
    return <Button type="button" icon="pi pi-eye" className="p-button-text" onClick={() => showChanges(rowData)} />;
  };

  return (
    <div>
      <DataTable stripedRows emptyMessage="Žiadne záznamy" value={history} loading={loading}>
        <Column field="type" header="Typ" body={v => formatType(v.type)} />
        <Column field="revisionId" header="ID" />
        <Column field="modifiedAt" header="Modifikované dňa" body={v => formatDateFromInstant(v.modifiedAt)} />
        <Column field="modifiedBy" header="Modifikoval" />
        <Column body={actionBodyTemplate} header="Akcie" />
      </DataTable>

      <Dialog
        header="Zmeny v zmluve"
        visible={dialogVisible}
        style={{ width: '60vw' }}
        breakpoints={{ '960px': '80vw', '641px': '95vw' }}
        onHide={() => setDialogVisible(false)}
      >
        {selectedRevision && <RevisionChanges revision={selectedRevision} />}
      </Dialog>
    </div>
  );
};

export default ContractHistoryTable;
